import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { NotificationsService, PendingNotificationsResponse } from '../../../services/notifications.service';
import { CustomerTaskTodayCountCacheService } from '../../../services/customer-task-today-count-cache.service';
import { TokenService } from '../../../core/services/token.service';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

/** Matches notification items returned by the notifications API. */
export interface CustomerTaskNotification {
  notificationID: string;
  customerTaskID: string;
  userID: string;
  avatarUserID: string;
  /** Display name for avatar initials fallback (assignee), when API sends it. */
  avatarDisplayName: string;
  title: string;
  message: string;
  createdOn: string;
  isRead: boolean;
  readAt: string | null;
  type: string;
  quoteID: string | null;
  customerID: string | null;
}

function extractRows(payload: unknown): unknown[] {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  const o = payload as Record<string, unknown>;
  const list = o['list'] ?? o['items'] ?? o['tasks'] ?? o['data'];
  return Array.isArray(list) ? list : [];
}

function asId(value: unknown): string {
  return String(value ?? '').trim();
}

/** Nested task object returned with the notification item (`customerTask: { assigneeUserID, ... }`). */
function getCustomerTaskObject(raw: Record<string, unknown>): Record<string, unknown> | null {
  const ct = raw['customerTask'];
  if (ct != null && typeof ct === 'object' && !Array.isArray(ct)) {
    return ct as Record<string, unknown>;
  }
  return null;
}

/** Accepts typical .NET / RFC UUID strings (not only RFC variant bits). */
function isGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function pickAvatarUserID(raw: Record<string, unknown>): string {
  const task = getCustomerTaskObject(raw);
  const assigneeUserID = asId(
    task?.['assigneeUserID'] ??
      task?.['assigneeUserId'] ??
      raw['assigneeUserID'] ??
      raw['assigneeUserId'],
  );
  const userID = asId(raw['userID'] ?? raw['userId'] ?? task?.['userID'] ?? task?.['userId']);

  // Prefer assignee (from `customerTask` or root): avatar = who the task is assigned to.
  if (isGuid(assigneeUserID)) {
    return assigneeUserID;
  }
  if (isGuid(userID)) {
    return userID;
  }
  return assigneeUserID || userID;
}

function pickAvatarDisplayName(raw: Record<string, unknown>): string {
  const task = getCustomerTaskObject(raw);
  const v =
    task?.['assigneeName'] ??
    task?.['assigneeUserName'] ??
    task?.['assigneeFullName'] ??
    raw['assigneeName'] ??
    raw['assigneeUserName'] ??
    raw['assigneeFullName'] ??
    raw['assignedToName'] ??
    raw['userName'] ??
    '';
  return String(v).trim();
}

export function normalizeTodayNotifications(payload: unknown): CustomerTaskNotification[] {
  return extractRows(payload)
    .map((raw) => {
      const r = raw as Record<string, unknown>;
      const task = getCustomerTaskObject(r);
      const notificationID = String(r['notificationID'] ?? r['notificationId'] ?? '').trim();
      if (!notificationID) {
        return null;
      }
      const quoteRaw = r['quoteID'] ?? r['quoteId'] ?? task?.['quoteID'] ?? task?.['quoteId'];
      const customerRaw =
        r['customerID'] ?? r['customerId'] ?? task?.['customerID'] ?? task?.['customerId'];
      return {
        notificationID,
        customerTaskID: String(
          r['customerTaskID'] ??
            r['customerTaskId'] ??
            task?.['customerTaskID'] ??
            task?.['customerTaskId'] ??
            '',
        ),
        userID: asId(r['userID'] ?? r['userId'] ?? task?.['userID'] ?? task?.['userId']),
        avatarUserID: pickAvatarUserID(r),
        avatarDisplayName: pickAvatarDisplayName(r),
        title: String(r['title'] ?? task?.['title'] ?? '').trim() || 'Task',
        message: String(r['message'] ?? ''),
        createdOn: String(r['createdOn'] ?? ''),
        isRead: Boolean(r['isRead'] ?? task?.['isRead']),
        readAt: (() => {
          const rawReadAt = r['readAt'] ?? task?.['readAt'];
          return rawReadAt == null || rawReadAt === '' ? null : String(rawReadAt);
        })(),
        type: String(r['type'] ?? ''),
        quoteID: quoteRaw != null && String(quoteRaw).trim() !== '' ? String(quoteRaw) : null,
        customerID:
          customerRaw != null && String(customerRaw).trim() !== '' ? String(customerRaw) : null,
      };
    })
    .filter((x): x is CustomerTaskNotification => x !== null);
}

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, OverlayPanelModule, UserAvatarComponent],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit {
  private static readonly PAGE_SIZE = 10;

  /**
   * IDs the user has successfully marked read (PATCH ok). Refetch often lags the DB or returns
   * `isRead` only on nested shapes without this, the unread dot flickers back after reload.
   */
  private readonly clientAcknowledgedReadIds = new Set<string>();

  private readonly notificationsService = inject(NotificationsService);
  private readonly customerTaskTodayCountCache = inject(CustomerTaskTodayCountCacheService);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);

  readonly pendingCountForAria = computed(
    () => this.customerTaskTodayCountCache.pendingCount() ?? 0,
  );

  @ViewChild('taskPanel') taskPanel?: OverlayPanel;

  readonly notifications = signal<CustomerTaskNotification[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly total = signal(0);

  readonly unreadList = computed(() => this.notifications().filter((n) => !n.isRead));

  /** Most recent first, regardless of read state; unread rows still show the blue dot. */
  readonly sortedNotificationsForDisplay = computed(() => {
    return [...this.notifications()].sort((a, b) => {
      const left = Date.parse(a.createdOn);
      const right = Date.parse(b.createdOn);
      if (Number.isNaN(left) && Number.isNaN(right)) {
        return 0;
      }
      if (Number.isNaN(left)) {
        return 1;
      }
      if (Number.isNaN(right)) {
        return -1;
      }
      return right - left;
    });
  });

  readonly pendingHeaderLabel = computed(() => {
    const n = this.customerTaskTodayCountCache.pendingCount() ?? 0;
    if (n <= 0) {
      return '';
    }
    return `${n} pending`;
  });

  readonly badgeText = computed(() => {
    const n = this.customerTaskTodayCountCache.pendingCount();
    if (n === null || n <= 0) {
      return '';
    }
    return n > 9 ? '9+' : String(n);
  });

  readonly hasMore = computed(() => this.notifications().length < this.total());

  ngOnInit(): void {
    this.customerTaskTodayCountCache.refreshIfStale();
  }

  private getPendingNotifications(
    skip: number,
    take = NotificationsComponent.PAGE_SIZE,
    forceRefresh = false,
  ): Observable<PendingNotificationsResponse | null> {
    return this.notificationsService.getPendingNotifications({ skip, take, forceRefresh });
  }

  isCurrentUser(userID: string): boolean {
    const id = this.tokenService.getJwtPayload()?.nameid;
    return id != null && String(id) === String(userID);
  }

  /**
   * `UserAvatarComponent` derives initials from `displayName`. The literal "You" becomes "YO"
   * (two-letter slice). Use JWT display name (or API assignee name) like profile does.
   */
  displayNameForAvatar(n: CustomerTaskNotification): string {
    const fromApi = String(n.avatarDisplayName ?? '').trim();
    if (this.isCurrentUser(n.avatarUserID)) {
      if (fromApi) {
        return fromApi;
      }
      const p = this.tokenService.getJwtPayload();
      return String(p.unique_name ?? p.email ?? '').trim();
    }
    return fromApi || String(n.title ?? '').trim();
  }

  isDueToday(iso: string): boolean {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return false;
    }
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }

  private applyClientReadAcknowledgements(
    list: CustomerTaskNotification[],
  ): CustomerTaskNotification[] {
    if (this.clientAcknowledgedReadIds.size === 0) {
      return list;
    }
    return list.map((item) =>
      this.clientAcknowledgedReadIds.has(item.notificationID)
        ? {
            ...item,
            isRead: true,
            readAt: item.readAt ?? new Date().toISOString(),
          }
        : item,
    );
  }

  private appendUniqueNotifications(list: CustomerTaskNotification[]): void {
    this.notifications.update((current) => {
      const existingIds = new Set(current.map((item) => item.notificationID));
      const next = list.filter((item) => !existingIds.has(item.notificationID));
      return [...current, ...next];
    });
  }

  onPanelShow(): void {
    this.loading.set(true);
    this.getPendingNotifications(0).subscribe((response) => {
      this.notifications.set(
        this.applyClientReadAcknowledgements(normalizeTodayNotifications(response)),
      );
      this.total.set(response?.total ?? 0);
      this.loading.set(false);
    });
  }

  loadMore(): void {
    if (this.loadingMore() || !this.hasMore()) {
      return;
    }

    this.loadingMore.set(true);
    this.getPendingNotifications(this.notifications().length).subscribe((response) => {
      this.appendUniqueNotifications(
        this.applyClientReadAcknowledgements(normalizeTodayNotifications(response)),
      );
      this.total.set(response?.total ?? this.total());
      this.loadingMore.set(false);
    });
  }

  onListScroll(event: Event): void {
    const element = event.target;
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const remainingScroll = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remainingScroll <= 48) {
      this.loadMore();
    }
  }

  togglePanel(event: Event): void {
    const panel = this.taskPanel;
    const opening = panel != null && !panel.overlayVisible;
    panel?.toggle(event);
    if (opening) {
      this.onPanelShow();
    }
  }

  onRowClick(n: CustomerTaskNotification, event: Event): void {
    this.onNotificationClick(n, event);
  }

  /** Immediate UI: hide unread dot / row styling before the PATCH returns. */
  private setNotificationReadLocally(notificationID: string, read: boolean): void {
    this.notifications.update((list) =>
      list.map((x) =>
        x.notificationID === notificationID
          ? { ...x, isRead: read, readAt: read ? new Date().toISOString() : null }
          : x,
      ),
    );
  }

  onNotificationClick(n: CustomerTaskNotification, event: Event): void {
    event.preventDefault();
    if (!n.isRead) {
      this.clientAcknowledgedReadIds.add(n.notificationID);
      this.setNotificationReadLocally(n.notificationID, true);
    }

    this.taskPanel?.hide();
    if (n.quoteID) {
      void this.router.navigate(['/quotes/summary', n.quoteID]);
    } else if (n.customerID) {
      void this.router.navigate(['/customers/edit', n.customerID]);
    }

    this.notificationsService
      .markNotificationRead(n.notificationID)
      .pipe(
        switchMap((res: unknown | null) => {
          this.customerTaskTodayCountCache.refresh();
          if (res === null) {
            return EMPTY;
          }
          return this.getPendingNotifications(
            0,
            Math.max(this.notifications().length, NotificationsComponent.PAGE_SIZE),
            true,
          );
        }),
      )
      .subscribe((response) => {
        if (response != null) {
          this.notifications.set(
            this.applyClientReadAcknowledgements(normalizeTodayNotifications(response)),
          );
          this.total.set(response.total);
        }
      });
  }
}
